import { FontIcon, Stack } from '@fluentui/react';
import React, { useState } from 'react';
import { IAnalysisResult, IReceipt } from '../../../shared/interfaces/IReceipAnalyzerEntities';

export type Column<T> = {
  key: keyof T;
  label?: string;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  width: number | string; // Optional width for the column
  total: boolean; // Indicates if this column should show a total
};

/**
 * Returns totals for columns marked with `total: true`.
 * Only sums numeric values.
 */
function getTotals<T extends Record<string, any>>(columns: Column<T>[], data: T[]): Record<string, number> {
  const totals: Record<string, number> = {};
  columns.forEach((col) => {
    if (col.total) {
      totals[String(col.key)] = data.reduce((sum, row) => {
        const val = row[col.key];
        return typeof val === "number" ? sum + val : sum;
      }, 0);
    }
  });
  return totals;
}

type GenericTableProps<T = IAnalysisResult> = {
  data: T[];
  columns: Column<T>[];
  filterKeys?: (keyof T)[];
  rowsPerPage?: number;
  header: boolean;
  total: boolean;
  externalFilter?: string;
};

function SummaryTable<T extends { id: number }>(
  props: GenericTableProps<T>
) {
  const {
    data,
    columns,
    filterKeys = [],
    rowsPerPage = 5,
    header,
    total,
    externalFilter,
  } = props;

  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [filterText, setFilterText] = useState("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Use externalFilter when provided, otherwise internal filterText
  const activeFilter = externalFilter !== undefined ? externalFilter : filterText;

  // Reset to page 1 when external filter changes
  React.useEffect(() => {
    if (externalFilter !== undefined) setCurrentPage(1);
  }, [externalFilter]);
  const gridTemplate = columns.map(col => typeof col.width === 'number' ? `${col.width}px` : col.width).join(' ');
  const gridTemplateColumns = columns.map(col =>
    typeof col.width === 'number' ? `${col.width}px` : col.width
  ).join(' ');

  const handleSort = (key: keyof T) => {
    if (key === sortKey) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const filteredData = activeFilter
    ? data.filter((row) =>
        filterKeys.some((key) =>
          String(row[key] ?? "")
            .toLowerCase()
            .includes(activeFilter.toLowerCase())
        )
      )
    : data;

  const sortedData = sortKey
    ? [...filteredData].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      })
    : filteredData;

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (

      <div className="custom-grid-table">

        {/* Header */}
        {
          header && 
          <div key={"header-row"} className="row header-row">
            <div className="grid" style={{ gridTemplateColumns: gridTemplateColumns }}>
                {
                  columns.map((col) => (
                    <span 
                      style={{ 
                      textAlign: "left", 
                      cursor: col.sortable ? "pointer" : "default" 
                      }}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      {col.label}
                      {col.sortable && sortKey === col.key && (
                      <FontIcon 
                        className='sort-icon'
                        iconName={sortOrder === "asc" ? "SortUp" : "SortDown"} 
                        style={{ marginLeft: 4 }}
                      />
                      )}
                    </span>
                  ))
                }
            </div>
          </div>
        }

        {/* Body rows */}
        {paginatedData?.map((item: any) => (
          <div key={item.item_id ?? item.id} className="content-row row">
            <div className="grid" style={{ gridTemplateColumns: gridTemplateColumns}}>
              {
                columns.map((col) => (
                    <span style={{ textAlign: "left" }}>
                      {}
                      {col.render ? col.render(item[col.key], item) : (typeof item[col.key] === "number" ? (item[col.key] as number).toFixed(2) : item[col.key])}
                    </span>
                ))
              } 
            </div>
          </div>
        ))}

        {/* Footer with totals */}
        {
          total && 

          <div key={"footer-row"} className="footer-row row">
            <div className="grid" style={{ gridTemplateColumns: gridTemplateColumns }}>
                {
                  columns.map((col) => {
                    const totalVal = getTotals(columns, sortedData)[String(col.key)];
                    return (
                      <span style={{ textAlign: "left" }}>
                        {col.total && totalVal != null ? (Number.isInteger(totalVal) ? totalVal : totalVal.toFixed(2)) : ""}
                      </span>
                    );
                  })
                }
            </div>
          </div>
        }
        
        {/* Pagination controls */}
        <Stack horizontal horizontalAlign="center" className="summary-table-pagination-container" styles={{ root: { alignItems: 'center', gap: 8 } }}>
            <FontIcon iconName="Previous" onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} />
            <span>{currentPage} of {totalPages}</span>
            <FontIcon iconName="Next" onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} />
        </Stack>
      </div>
  )
}

export default SummaryTable;
